import { useEffect, useRef } from 'react'
import styles from './AiddBanner.module.scss'

const DESKTOP_POSTER = '/img/common/aidd_banner_desktop_poster.webp'
const MOBILE_POSTER = '/img/common/aidd_banner_mobile_poster.webp'
const DESKTOP_VIDEO = '/video/aidd_banner_desktop.mp4'
const MOBILE_VIDEO = '/video/aidd_banner_mobile.mp4'
const SP_MAX_WIDTH = 719

type NetworkInformation = {
  saveData?: boolean
  effectiveType?: string
  addEventListener?: (type: 'change', listener: () => void) => void
  removeEventListener?: (type: 'change', listener: () => void) => void
}

const AiddBanner: React.FC = () => {
  const bannerRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const mobilePosterRef = useRef<HTMLSourceElement>(null)

  useEffect(() => {
    const banner = bannerRef.current
    const video = videoRef.current
    const mobilePoster = mobilePosterRef.current
    if (!banner || !video) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection
    let visible = true
    let layout = ''
    let failed = false

    const canAnimate = () =>
      !reducedMotion.matches &&
      !connection?.saveData &&
      connection?.effectiveType !== 'slow-2g' &&
      connection?.effectiveType !== '2g'

    const clearVideo = () => {
      video.pause()
      banner.removeAttribute('data-video-ready')
      if (video.hasAttribute('src')) {
        video.removeAttribute('src')
        video.preload = 'none'
        video.load()
      }
    }

    const selectMedia = () => {
      const next = banner.clientWidth <= SP_MAX_WIDTH ? 'mobile' : 'desktop'
      if (next === layout) return
      layout = next
      failed = false
      if (mobilePoster) mobilePoster.media = layout === 'mobile' ? 'all' : 'not all'
      clearVideo()
    }

    const syncPlayback = () => {
      if (!canAnimate()) {
        clearVideo()
        return
      }
      if (failed || !visible || document.hidden) {
        video.pause()
        return
      }
      if (!video.hasAttribute('src')) {
        video.preload = 'auto'
        video.src = layout === 'mobile' ? MOBILE_VIDEO : DESKTOP_VIDEO
        video.load()
      }
      video.muted = true
      video.play().catch(() => {})
    }

    const onPlaying = () => {
      if (canAnimate() && visible && !document.hidden) banner.setAttribute('data-video-ready', '')
    }
    const onError = () => {
      failed = true
      banner.removeAttribute('data-video-ready')
    }

    video.addEventListener('playing', onPlaying)
    video.addEventListener('error', onError)
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', syncPlayback)
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(syncPlayback)
    }
    if (connection && typeof connection.addEventListener === 'function') {
      connection.addEventListener('change', syncPlayback)
    }
    document.addEventListener('visibilitychange', syncPlayback)

    let intersectionObserver: IntersectionObserver | null = null
    if (typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        visible = entry.isIntersecting
        syncPlayback()
      })
      intersectionObserver.observe(banner)
    }

    let resizeObserver: ResizeObserver | null = null
    const onWindowResize = () => {
      selectMedia()
      syncPlayback()
    }
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        selectMedia()
        syncPlayback()
      })
      resizeObserver.observe(banner)
    } else {
      window.addEventListener('resize', onWindowResize)
    }

    selectMedia()
    syncPlayback()

    return () => {
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('error', onError)
      if (typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', syncPlayback)
      } else if (typeof reducedMotion.removeListener === 'function') {
        reducedMotion.removeListener(syncPlayback)
      }
      if (connection && typeof connection.removeEventListener === 'function') {
        connection.removeEventListener('change', syncPlayback)
      }
      document.removeEventListener('visibilitychange', syncPlayback)
      if (intersectionObserver) intersectionObserver.disconnect()
      if (resizeObserver) resizeObserver.disconnect()
      else window.removeEventListener('resize', onWindowResize)
      video.pause()
    }
  }, [])

  return (
    <section className={styles.aiddBanner} ref={bannerRef} aria-label="AI駆動開発">
      <div className={styles.media} aria-hidden="true">
        <picture>
          <source ref={mobilePosterRef} media={`(max-width: ${SP_MAX_WIDTH}px)`} srcSet={MOBILE_POSTER} />
          <img
            className={styles.poster}
            src={DESKTOP_POSTER}
            alt=""
            width={1280}
            height={432}
            loading="eager"
            decoding="async"
          />
        </picture>
        <video className={styles.video} ref={videoRef} muted loop playsInline preload="none" tabIndex={-1} />
      </div>
      <a className={styles.link} href="/services/aidd/" target="_blank" rel="noopener noreferrer">
        <h2 className={styles.heading}>
          <span className={styles.intro}>AI駆動開発で、</span>
          <span className={styles.message}>
            <span>組織の競争力を</span>
            <span>引き上げる。</span>
          </span>
        </h2>
        <span className={styles.cta}>
          詳しく見る
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">
            <path
              d="M3 10h14M10 3l7 7-7 7"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className={styles.wordmark} lang="en" aria-hidden="true">
          AI-DRIVEN DEVELOPMENT
        </span>
      </a>
    </section>
  )
}

export default AiddBanner
